import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Building2, CheckCircle2, CreditCard, MapPin, Phone, Receipt, ShieldAlert, Unlink2, UserRound } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";
import { StatusBadge } from "./common";
import { api, errMsg, fmtDate, fmtINR } from "../lib/api";
import { pokeLiveCollections } from "../lib/liveCollections";

export const NO_CONTACT_MESSAGE = "No collector assigned — please contact your municipal office.";

/** Phase 206 — remove a wrongly linked property from the citizen's account (confirmation step, audited server-side). */
export function UnlinkPropertyButton({ code, prop, onUnlinked, testId, size = "sm", variant = "ghost" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const tid = testId || `unlink-${prop.pin}`;
  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/t/${code}/citizen/unlink-property`, { property_id: prop.id, pin: prop.pin });
      pokeLiveCollections();
      toast.success(`${prop.pin} removed from your account`, { description: "You can link it again any time with its PIN." });
      setOpen(false);
      onUnlinked?.(prop);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <AlertDialog open={open} onOpenChange={(v) => { if (!busy) setOpen(v); }}>
      <AlertDialogTrigger asChild>
        <Button size={size} variant={variant} className="text-muted-foreground hover:text-destructive" data-testid={`${tid}-button`} aria-label={`Unlink property ${prop.pin}`}>
          <Unlink2 className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> Unlink
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid={`${tid}-dialog`}>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove {prop.pin} from your account?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p><span className="font-medium text-foreground">{prop.owner_name || "This property"}</span>{prop.address ? ` · ${prop.address}` : ""}</p>
              <p>Its dues, receipts and collector contact will no longer appear on your dashboard or in My Properties. The municipality's property record is not changed, and you can link the property again at any time by entering its PIN.</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid={`${tid}-cancel`}>Keep it</AlertDialogCancel>
          <AlertDialogAction onClick={(e) => { e.preventDefault(); confirm(); }} disabled={busy}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`${tid}-confirm`}>
            {busy ? "Removing…" : "Yes, unlink"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Phase 205 — who the citizen should call for this property: Field Collector → Supervisor → municipal office. */
export function PropertyContactBlock({ contact, message, testId }) {
  if (!contact) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/40 px-3 py-2.5 flex items-start gap-2" data-testid={`${testId}-none`}>
        <ShieldAlert className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" aria-hidden="true" />
        <p className="text-xs text-muted-foreground">{message || NO_CONTACT_MESSAGE}</p>
      </div>
    );
  }
  return (
    <div className="rounded-lg border bg-accent/40 px-3 py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid={testId}>
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <UserRound className="h-4 w-4" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] text-muted-foreground" data-testid={`${testId}-kind`}>Your {contact.label}</p>
          <p className="text-sm font-medium truncate" data-testid={`${testId}-name`}>
            {contact.name}{contact.employee_id ? <span className="text-muted-foreground font-normal"> ({contact.employee_id})</span> : null}
          </p>
        </div>
      </div>
      <Button asChild size="sm" variant="outline" className="shrink-0">
        <a href={`tel:${contact.mobile}`} data-testid={`${testId}-call`} aria-label={`Call ${contact.label} ${contact.name} on ${contact.mobile}`}>
          <Phone className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> <span className="tabular-nums">{contact.mobile}</span>
        </a>
      </Button>
    </div>
  );
}

/** Dashboard card: PIN · address · zone · ward · type · dues / last payment · contact. */
export function CitizenPropertyCard({ code, prop, testId, compact = false, action = null, onUnlinked = null }) {
  const bal = prop.demand_summary?.balance || 0;
  const paidUp = bal <= 0;
  const lp = prop.last_payment;
  const tid = testId || `citizen-property-${prop.pin}`;
  return (
    <Card className="shadow-sm h-full" data-testid={tid}>
      <CardContent className="p-4 flex flex-col gap-3 h-full">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-mono font-semibold tracking-wide" data-testid={`${tid}-pin`}>{prop.pin}</p>
            {prop.owner_name && <p className="text-sm font-medium truncate" data-testid={`${tid}-owner`}>{prop.owner_name}</p>}
            <p className="text-sm text-muted-foreground flex items-start gap-1 mt-0.5" data-testid={`${tid}-address`}>
              <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" />
              <span>{prop.address || "Address not recorded"}</span>
            </p>
          </div>
          {paidUp ? (
            <Badge variant="outline" className="gap-1 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/20 shrink-0" data-testid={`${tid}-paid-up`}>
              <CheckCircle2 className="h-3 w-3" aria-hidden="true" /> Paid up
            </Badge>
          ) : <StatusBadge status={prop.payment_status} />}
        </div>

        <div className="grid grid-cols-3 gap-2 text-xs" data-testid={`${tid}-meta`}>
          <div className="rounded-md bg-muted/50 px-2 py-1.5"><p className="text-[10px] text-muted-foreground">Zone</p><p className="font-medium truncate" data-testid={`${tid}-zone`}>{prop.zone_label || "—"}</p></div>
          <div className="rounded-md bg-muted/50 px-2 py-1.5"><p className="text-[10px] text-muted-foreground">Ward</p><p className="font-medium truncate" data-testid={`${tid}-ward`}>{prop.ward_label || "—"}</p></div>
          <div className="rounded-md bg-muted/50 px-2 py-1.5"><p className="text-[10px] text-muted-foreground">Property type</p><p className="font-medium truncate" data-testid={`${tid}-type`}>{prop.property_type_label || prop.usage_type_label || "—"}</p></div>
        </div>

        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[11px] text-muted-foreground">{paidUp ? "Outstanding dues" : "Total outstanding dues"}</p>
            <p className={`text-xl font-semibold tabular-nums ${paidUp ? "text-[hsl(var(--success))]" : ""}`} data-testid={`${tid}-due`}>{fmtINR(bal)}</p>
          </div>
          <div className="text-right">
            <p className="text-[11px] text-muted-foreground flex items-center gap-1 justify-end"><Receipt className="h-3 w-3" aria-hidden="true" /> Last payment</p>
            {lp ? (
              <p className="text-xs" data-testid={`${tid}-last-payment`}>
                <span className="font-mono">{lp.receipt_number}</span> · {fmtDate(lp.payment_date)} · <span className="tabular-nums">{fmtINR(lp.amount)}</span>
              </p>
            ) : <p className="text-xs text-muted-foreground" data-testid={`${tid}-last-payment`}>No payment recorded yet</p>}
          </div>
        </div>

        <PropertyContactBlock contact={prop.contact} message={prop.contact_message} testId={`${tid}-contact`} />

        {!compact && (
          <div className="flex items-center gap-2 flex-wrap mt-auto pt-1">
            <Button asChild size="sm" variant={paidUp ? "outline" : "default"}>
              <Link to={`/${code}/portal/properties`} data-testid={`${tid}-view`}>
                {paidUp ? <><Building2 className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> View details</> : <><CreditCard className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> View & pay</>}
              </Link>
            </Button>
            {action}
            {onUnlinked && <span className="ml-auto"><UnlinkPropertyButton code={code} prop={prop} onUnlinked={onUnlinked} testId={`${tid}-unlink`} /></span>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
